import { startTransition, useCallback, useEffect, useReducer, useRef } from 'react';
import { fetcher as defaultFetcher } from '../fetcher/fetcher';
/**
 * Shallow result equality: reference-equal, or same-length arrays whose
 * elements are reference-equal. Keeps `select` output (e.g. `getAll().filter`,
 * which builds a new array each call) reference-stable when nothing changed.
 */ const shallowResultEqual = (a, b)=>{
    if (Object.is(a, b)) {
        return true;
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        for(let i = 0; i < a.length; i += 1){
            if (!Object.is(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }
    return false;
};
/**
 * React-Query-shaped read backed by a Collection + Fetcher. The fetch is
 * deduped/staleness-gated by the Fetcher and lands in the Collection (the
 * cache); `data` is derived reactively from the Collection. Replaces
 * `useQuery` for collection-backed data with no Redux / React Query.
 */ export function useCollectionQuery(options) {
    const { collection, queryKey, queryFn, select, enabled = true, staleMs = 0, deps = [], fetcher = defaultFetcher } = options;
    const { isEqual = shallowResultEqual } = options;
    // Stable refs so the fetch effect doesn't re-run on inline-fn identity.
    const queryFnRef = useRef(queryFn);
    queryFnRef.current = queryFn;
    const selectRef = useRef(select);
    selectRef.current = select;
    const isEqualRef = useRef(isEqual);
    isEqualRef.current = isEqual;
    const [, bump] = useReducer((n)=>n + 1, 0);
    // Reference-stable derived data. `select` (e.g. `getAll().filter`) builds a
    // new value each call, so computing it inline on every render would hand
    // consumers a fresh reference every time → effects/memos that depend on
    // `data` re-fire forever ("Maximum update depth exceeded"). Instead cache the
    // result and only swap the reference when it actually changes.
    const dataRef = useRef(undefined);
    const initialized = useRef(false);
    if (!initialized.current) {
        dataRef.current = selectRef.current(collection);
        initialized.current = true;
    }
    // Recompute on collection change (and when the query key changes, which
    // tracks the select's parameters, e.g. a different projectId/taskId).
    useEffect(()=>{
        // Initial recompute is urgent (first paint of this query's data).
        const next = selectRef.current(collection);
        if (!isEqualRef.current(dataRef.current, next)) {
            dataRef.current = next;
            bump();
        }
        // Subsequent recomputes (driven by collection edits / WS refetches) are
        // marked as a transition: an optimistic edit must NOT block the interaction
        // or paint while a large derived list re-renders. The edited control stays
        // responsive (its own optimistic state / per-id useCollectionItem), and
        // React renders the new derived `data` at low priority, interrupting it for
        // urgent updates (typing, clicks). The selector still runs synchronously —
        // keep it cheap (index-backed) for large collections.
        return collection.subscribeToChanges(()=>{
            const recomputed = selectRef.current(collection);
            if (!isEqualRef.current(dataRef.current, recomputed)) {
                dataRef.current = recomputed;
                startTransition(()=>bump());
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        collection,
        queryKey
    ]);
    const createFetchState = useCallback((key)=>({
            isError: false,
            error: null,
            loading: enabled && !fetcher.isFresh(key),
            fetched: fetcher.isFresh(key)
        }), [
        enabled,
        fetcher
    ]);
    const stateRef = useRef(createFetchState(queryKey));
    const queryKeyRef = useRef(queryKey);
    if (queryKeyRef.current !== queryKey) {
        queryKeyRef.current = queryKey;
        stateRef.current = createFetchState(queryKey);
    }
    const markFetchSettled = useCallback((next)=>{
        stateRef.current = {
            isError: next.isError,
            error: next.error,
            loading: false,
            fetched: true
        };
        bump();
    }, []);
    const doFetch = useCallback((force)=>{
        if (!enabled) {
            return Promise.resolve();
        }
        if (!force && fetcher.isFresh(queryKey)) {
            markFetchSettled({
                isError: false,
                error: null
            });
            return Promise.resolve();
        }
        stateRef.current = {
            ...stateRef.current,
            loading: true
        };
        bump();
        return fetcher.fetch(queryKey, ()=>queryFnRef.current(), {
            staleMs
        }, force).then(()=>{
            markFetchSettled({
                isError: false,
                error: null
            });
        }).catch((error)=>{
            markFetchSettled({
                isError: true,
                error
            });
        });
    }, [
        enabled,
        queryKey,
        staleMs,
        fetcher,
        markFetchSettled
    ]);
    useEffect(()=>{
        doFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        queryKey,
        enabled,
        ...deps
    ]);
    const refetch = useCallback(async ()=>{
        await doFetch(true);
    }, [
        doFetch
    ]);
    return {
        data: dataRef.current,
        isLoading: stateRef.current.loading,
        isFetching: fetcher.isFetching(queryKey),
        isFetched: stateRef.current.fetched,
        isInitialLoading: stateRef.current.loading && !stateRef.current.fetched,
        isSuccess: stateRef.current.fetched && !stateRef.current.isError,
        isError: stateRef.current.isError,
        error: stateRef.current.error,
        // RQ-compatible status string (`useQuery().status`) so read-flipped
        // consumers that branch on `status === 'success'` keep working.
        status: stateRef.current.isError ? 'error' : stateRef.current.fetched ? 'success' : 'loading',
        refetch
    };
}
